import { useMemo } from 'react';

import { BillingPlan } from './billing';

import { bindActionCreators, useDispatch } from 'third-party';

import {
  SuperAdminOrganizationFilter,
  billingPlanEndpoint,
  superAdminAllOrganizationsEndpoint,
  superAdminOrgCreditEndpoint,
  superAdminOrgDisableEndpoint,
  superAdminOrgEnableEndpoint,
  superAdminOrgStatsEndpoint,
  superAdminOrgUsageEndpoint,
} from 'constants/endpoints';

import { httpMethod, simplifyBuilder } from 'utils/sra';

import { APIOrganization } from 'types/api';

export enum CreditGrantType {
  None = 0,
  Monthly = 1,
  Lifetime = 2,
  Unlimited = 3,
}

export type DateCheckStat = {
  date: string;
  count: number;
};

export type DateMoneyStat = {
  date: string;
  amount: number;
};

export type StatusStat = {
  status: string;
  count: number;
};

export type OrgStats = {
  checksCreated: DateCheckStat[];
  checksPrinted: DateCheckStat[];
  moneyIssued: DateMoneyStat[];
  statusBreakdown: StatusStat[];
};

export type UserUsage = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  used: number;
  remaining: number;
};

export type OrgUsage = {
  limit: number;
  totalUsed: number;
  remaining: number;
  grantType: CreditGrantType;
  isLifetime: boolean;
  isUnlimited: boolean;
  userCount: number;
  isDisabled: boolean;
  userUsages: UserUsage[];
  planCode: string | null;
  planName: string | null;
  monthlyIncluded: number;
  monthlyUsed: number;
  monthlyRemaining: number;
  packRemaining: number;
};

export type OrgCreditFeatures = {
  maxBankAccounts: number;
  showBadge: boolean;
  hasCheckDesigner: boolean;
  hasCustomEmailTemplates: boolean;
  hasApiAccess: boolean;
  hasPrioritySupport: boolean;
};

export type OrgCredit = OrgCreditFeatures & {
  id: string;
  organizationId: string;
  value: number;
  grantType: CreditGrantType;
  isLifetime: boolean;
  isUnlimited: boolean;
  isManualGrant: boolean;
  planCode: string | null;
  planName: string | null;
  createdOn: string;
  startDate: string | null;
  endDate: string | null;
  isActive: boolean;
  isExpired: boolean;
};

export type SuperAdminState = {
  allOrganizations: APIOrganization[];
  allOrganizationsLoaded: boolean;
  loadAllOrganizationsPending: boolean;
  selectedOrganizationId: string | null;
  orgStats: OrgStats | null;
  loadOrgStatsPending: boolean;
  orgUsage: OrgUsage | null;
  loadOrgUsagePending: boolean;
  orgCredit: OrgCredit | null;
  orgPlans: BillingPlan[];
  loadOrgCreditPending: boolean;
  setOrgCreditPending: boolean;
  disableOrgPending: boolean;
};

export const superAdminState: SuperAdminState = {
  allOrganizations: [],
  allOrganizationsLoaded: false,
  loadAllOrganizationsPending: false,
  selectedOrganizationId: null,
  orgStats: null,
  loadOrgStatsPending: false,
  orgUsage: null,
  loadOrgUsagePending: false,
  orgCredit: null,
  orgPlans: [],
  loadOrgCreditPending: false,
  setOrgCreditPending: false,
  disableOrgPending: false,
};

const builder = simplifyBuilder(superAdminState, {});

export const loadAllOrganizations = builder.createServerAction(
  (filter?: SuperAdminOrganizationFilter) => ({
    name: 'loadAllOrganizations',
    url: superAdminAllOrganizationsEndpoint(filter),
    method: httpMethod.get,
    onSuccess: (_state: SuperAdminState, payload: APIOrganization[]) => ({
      allOrganizations: payload,
      allOrganizationsLoaded: true,
    }),
  }),
);

export const loadOrgStats = builder.createServerAction(
  (organizationId: string, period: string) => ({
    name: 'loadOrgStats',
    url: superAdminOrgStatsEndpoint(organizationId, period),
    method: httpMethod.get,
    onSuccess: (_state: SuperAdminState, payload: OrgStats) => ({
      orgStats: payload,
    }),
  }),
);

export const loadOrgUsage = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadOrgUsage',
    url: superAdminOrgUsageEndpoint(organizationId),
    method: httpMethod.get,
    onSuccess: (_state: SuperAdminState, payload: OrgUsage) => ({
      orgUsage: payload,
    }),
  }),
);

export const loadOrgCredit = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadOrgCredit',
    url: superAdminOrgCreditEndpoint(organizationId),
    method: httpMethod.get,
    onSuccess: (_state: SuperAdminState, payload: OrgCredit) => ({
      orgCredit: payload ?? null,
    }),
  }),
);

export const loadOrgPlans = builder.createServerAction(
  (organizationId: string) => ({
    name: 'loadOrgPlans',
    url: billingPlanEndpoint(organizationId),
    method: httpMethod.get,
    onSuccess: (_state: SuperAdminState, payload: BillingPlan[]) => ({
      orgPlans: payload,
    }),
  }),
);

export const setOrgCredit = builder.createServerAction(
  (
    organizationId: string,
    value: number,
    grantType: CreditGrantType,
    startDate: string | null,
    endDate: string | null,
    planCode: string | null = null,
    features: OrgCreditFeatures | null = null,
  ) => ({
    name: 'setOrgCredit',
    url: superAdminOrgCreditEndpoint(organizationId),
    method: httpMethod.post,
    body: {
      value,
      grantType,
      startDate,
      endDate,
      planCode,
      ...features,
    },
    onSuccess: (_state: SuperAdminState, payload: OrgCredit) => ({
      orgCredit: payload,
    }),
  }),
);

export const disableOrg = builder.createServerAction(
  (organizationId: string) => ({
    name: 'disableOrg',
    url: superAdminOrgDisableEndpoint(organizationId),
    method: httpMethod.post,
    onSuccess: () => ({}),
  }),
);

export const enableOrg = builder.createServerAction(
  (organizationId: string) => ({
    name: 'enableOrg',
    url: superAdminOrgEnableEndpoint(organizationId),
    method: httpMethod.post,
    onSuccess: () => ({}),
  }),
);

/**
 * Drops the figures read for one organization, so the admin pages cannot show the previous one's
 * usage or entitlement while the next request is still in flight.
 */
export const clearSuperAdminOrganization = builder.createReduxAction(() => ({
  name: 'clearSuperAdminOrganization',
  updater: () => ({
    orgStats: null,
    orgUsage: null,
    orgCredit: null,
    orgPlans: [],
  }),
}));

export const selectSuperAdminOrganization = builder.createReduxAction(
  (organizationId: string | null) => ({
    name: 'selectSuperAdminOrganization',
    updater: () => ({
      selectedOrganizationId: organizationId,
      orgStats: null,
    }),
  }),
);

export const useSuperAdmin = () => {
  const dispatch = useDispatch();

  return useMemo(
    () =>
      bindActionCreators(
        {
          loadAllOrganizations,
          loadOrgStats,
          loadOrgUsage,
          loadOrgCredit,
          loadOrgPlans,
          setOrgCredit,
          disableOrg,
          enableOrg,
          selectSuperAdminOrganization,
          clearSuperAdminOrganization,
        },
        dispatch,
      ),
    [dispatch],
  );
};

export const superAdminReducer = builder.getReducers();
